/**
 * The link card of a post being written or edited (the composer, the post editor): the first address in the
 * text gets a preview card; a bare address (not written as markdown) then leaves the text and the card is the
 * link. Removing the card puts the address back. RESET(preview) starts again, e.g. from a post's own card.
 * Backend: GET /interact/posts/link-preview/?url=
 */
import { useEffect, useRef, useState } from 'react';
import { axiosService } from 'shared/helpers/axios';
import { isBareAddress, looksLikeImage, removeAddress, withoutImages } from 'shared/helpers/postText';
import { parseYouTube } from 'shared/helpers/youtube';

const URL_RE = /https?:\/\/[^\s<>"{}|\\^`[\]]+/i;

export default function useLinkPreview(content, setContent) {
  const [linkPreview, setLinkPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const lastFetchedUrl = useRef('');
  const dismissedUrl = useRef('');
  const attachedUrl = useRef('');      // the address the card stands for, no longer in the text

  useEffect(() => {
    const match = withoutImages(content || '').match(URL_RE);
    const url = match ? match[0].replace(/[).,;:!?'"]+$/, '') : '';   // not the punctuation ending the sentence
    if (!url) {
      if (!attachedUrl.current) {            // a card standing in for its removed address stays
        setLinkPreview(null);
        lastFetchedUrl.current = '';
      }
      return undefined;
    }
    if (url === lastFetchedUrl.current || url === dismissedUrl.current) return undefined;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await axiosService.get(`/interact/posts/link-preview/?url=${encodeURIComponent(url)}`);
        setLinkPreview(res.data);
      } catch {
        // the page could not be read (blocked, no metadata): a plain card with the site, so every link shows
        const video = parseYouTube(url);
        let host = url;
        try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { /* keep the url */ }
        setLinkPreview(looksLikeImage(url)
          ? { url, kind: 'image', title: '', description: '', site_name: host, image: url }
          : { url, title: '', description: '', site_name: video ? 'YouTube' : host,
              image: video ? `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg` : '' });
      } finally {
        lastFetchedUrl.current = url;
        setPreviewLoading(false);
        if (isBareAddress(content, url)) {
          const previous = attachedUrl.current;      // a card already standing in for another address
          attachedUrl.current = url;
          setContent((text) => {
            const without = removeAddress(text, url);
            return previous && previous !== url && !without.includes(previous) ? `${without}\n${previous}`.trim() : without;
          });
        }
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [content]);   // eslint-disable-line react-hooks/exhaustive-deps

  const dismissCard = () => {
    const url = lastFetchedUrl.current;
    dismissedUrl.current = url;
    if (attachedUrl.current && attachedUrl.current === url) {
      setContent((text) => (text.includes(url) ? text : `${text.trimEnd()} ${url}`.trim()));
    }
    attachedUrl.current = '';
    setLinkPreview(null);
  };

  /** Start over: no card, or PREVIEW (a post's own card, for its address in TEXT or standing in for it). */
  const reset = (preview = null, text = '') => {
    setLinkPreview(preview);
    lastFetchedUrl.current = preview?.url || '';
    dismissedUrl.current = '';
    attachedUrl.current = preview?.url && !text.includes(preview.url) ? preview.url : '';
  };

  return { linkPreview, previewLoading, dismissCard, reset };
}
